import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Header from './components/Header';
import Footer from './components/Footer';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import PatientDashboard from './pages/PatientDashboard';
import BookAppointment from './pages/BookAppointment';
import ChatbotPage from './pages/ChatbotPage';
import MessagesPage from './pages/MessagesPage';
import PharmacyPage from './pages/PharmacyPage';
import ProgramsPage from './pages/ProgramsPage';
import ProgramRegistrationPage from './pages/ProgramRegistrationPage';
import ConditionsPage from './pages/ConditionsPage';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Layout Component
const Layout = ({ children, showFooter = true }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {children}
      </main>
      {showFooter && <Footer />}
    </div>
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={
        <Layout>
          <LandingPage />
        </Layout>
      } />
      
      <Route path="/login" element={
        <Layout showFooter={false}>
          <LoginPage />
        </Layout>
      } />
      
      <Route path="/register" element={
        <Layout showFooter={false}>
          <RegisterPage />
        </Layout>
      } />

      <Route path="/book-appointment" element={
        <Layout>
          <BookAppointment />
        </Layout>
      } />

      <Route path="/chatbot" element={
        <Layout showFooter={false}>
          <ChatbotPage />
        </Layout>
      } />

      <Route path="/programs" element={
        <Layout>
          <ProgramsPage />
        </Layout>
      } />

      <Route path="/register-program" element={
        <Layout>
          <ProgramRegistrationPage />
        </Layout>
      } />

      <Route path="/conditions" element={
        <Layout>
          <ConditionsPage />
        </Layout>
      } />

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout>
            <PatientDashboard />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/appointments" element={
        <ProtectedRoute>
          <Layout>
            <PatientDashboard />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/messages" element={
        <ProtectedRoute>
          <Layout showFooter={false}>
            <MessagesPage />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/pharmacy" element={
        <ProtectedRoute>
          <Layout>
            <PharmacyPage />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout>
            <PatientDashboard />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/insurance" element={
        <ProtectedRoute>
          <Layout>
            <PatientDashboard />
          </Layout>
        </ProtectedRoute>
      } />

      {/* Payment Routes */}
      <Route path="/payment/success" element={
        <Layout>
          <div className="min-h-screen bg-secondary pt-24 pb-12 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold mb-2">Payment Successful!</h1>
              <p className="text-muted-foreground">Your payment has been processed successfully.</p>
            </div>
          </div>
        </Layout>
      } />

      <Route path="/payment/cancel" element={
        <Layout>
          <div className="min-h-screen bg-secondary pt-24 pb-12 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold mb-2">Payment Cancelled</h1>
              <p className="text-muted-foreground">Your payment was cancelled.</p>
            </div>
          </div>
        </Layout>
      } />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
